import React from 'react';
import classnames from 'classnames';
import styles from './CreateContactModal.module.scss';
import { FormControl } from 'react-bootstrap';
import { Control, Controller } from 'react-hook-form';
import { DataVendor } from 'v2/models';
import FormLabel from 'v2/form/FormLabel/FormLabel';
import { GeneralSelect } from 'v2/form/CustomSelectors/GeneralSelect';
import { TranslationKeys, PostProcessKeys } from 'translations';
import { useTranslation } from 'react-i18next';

import { useInternalDesignationOptions } from 'v2/utils/ContactUtils';
import QuestionMarkToolTip from 'v2/common/QuestionMarkToolTip/QuestionMarkToolTip';

export interface ContactFormFields {
  email: string;
  first_name: string | null;
  last_name: string | null;
  title: string | null;
  company_name: string | null;
  data_vendor: number | null;
  internal_designation: boolean;
}

interface ContactFormProps {
  control: Control<ContactFormFields>;
  readOnly?: boolean;
  dataVendor?: DataVendor;
}

const ContactFormForm = ({
  control,
  dataVendor,
  readOnly = false,
}: ContactFormProps) => {
  const { t } = useTranslation();
  const internalDesignationOptions = useInternalDesignationOptions();
  const { register } = control;
  const questionMarkToolTipHelpText =
    `Specifies if this is a ${t(TranslationKeys.contact_one)} inside` +
    ` or outside of your ${t(TranslationKeys.company_one)}.`;

  return (
    <div
      className={classnames({
        [styles.readOnlyForm]: readOnly,
      })}
    >
      <div className={classnames(styles.contactAdditionalForm)}>
        <div>
          <FormLabel label={`First Name`} required={false} />
          <div className={classnames(styles.inputContainer)}>
            <FormControl
              type="text"
              readOnly={readOnly}
              placeholder="First Name"
              aria-label="first-name-input"
              autoComplete="off"
              maxLength={255}
              data-cy="contact:create-name-field"
              {...register('first_name', { value: null })}
            />
          </div>
        </div>
        <div>
          <FormLabel label={`Last Name`} required={false} />
          <div className={classnames(styles.inputContainer)}>
            <FormControl
              type="text"
              readOnly={readOnly}
              placeholder="Last Name"
              aria-label="last-name-input"
              autoComplete="off"
              maxLength={255}
              data-cy="contact:create-lastname-field"
              {...register('last_name', { value: null })}
            />
          </div>
        </div>
        <div>
          <FormLabel
            label={t(TranslationKeys.company_one, {
              postProcess: PostProcessKeys.TitleCase,
            })}
            required={false}
          />
          <div className={classnames(styles.inputContainer)}>
            {dataVendor ? (
              <>
                <FormControl
                  type="text"
                  readOnly={true}
                  value={dataVendor.name}
                  aria-label="data-vendor-input"
                />
                <FormControl
                  type="hidden"
                  aria-label="data-vendor-hidden-input"
                  {...register('data_vendor')}
                  value={dataVendor.id}
                />
              </>
            ) : (
              <FormControl
                type="text"
                readOnly={readOnly}
                placeholder={t(TranslationKeys.company_one, {
                  postProcess: PostProcessKeys.TitleCase,
                })}
                aria-label="company-name-input"
                autoComplete="off"
                maxLength={255}
                data-cy="contact:create-company-name-field"
                {...register('company_name', { value: null })}
              />
            )}
          </div>
        </div>
        <div>
          <FormLabel label={`Title`} required={false} />
          <div className={classnames(styles.inputContainer)}>
            <FormControl
              type="text"
              readOnly={readOnly}
              placeholder="Title"
              aria-label="title-input"
              autoComplete="off"
              maxLength={255}
              data-cy="contact:create-title-field"
              {...register('title', { value: null })}
            />
          </div>
        </div>
        <div>
          <div className="d-flex align-items-baseline">
            <FormLabel label={`Contact Affiliation`} required={false} />
            <QuestionMarkToolTip
              styles={styles}
              helpText={questionMarkToolTipHelpText}
              trigger={readOnly ? 'click' : undefined}
              disabled={readOnly}
            />
          </div>

          <div className={classnames(styles.inputContainer)}>
            <Controller
              name="internal_designation"
              control={control}
              rules={{
                required: false,
              }}
              render={({ field: { onChange } }) => {
                return (
                  <GeneralSelect
                    options={internalDesignationOptions}
                    handleChange={(option) => onChange(option?.value)}
                    placeHolder="Select Affiliation"
                    disabled={readOnly}
                    customStyles={{
                      control: {
                        width: '100%',
                        borderColor: 'var(--light-gray)',
                      },
                      dropdownIndicator: {
                        right: '10px',
                        justifyContent: 'flex-end',
                      },
                    }}
                  />
                );
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactFormForm;
